/**
 * SPRINT 4J: Unified Checkout Page
 * Multi-vendor order checkout with delivery fee breakdown
 * 
 * SPRINT 4O: Enhanced error handling, offline protection, idempotency
 * SPRINT 5B: Location & Payment Policy Engine integration
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Package, CreditCard, AlertTriangle, CheckCircle, ArrowLeft, WifiOff, RefreshCw, MapPinned, Navigation } from 'lucide-react';
import { useCartStore } from '@/store/cart';
import { ordersApi } from '@/lib/api';
import { logger } from '@/lib/logger';
import { useOnlineStatus } from '@/components/OfflineBanner';
import { useToastStore } from '@/store/toasts';
import { LocationPicker, type LocationData } from '@/components/location/LocationPicker';
import { AddressAutocomplete, type AddressData } from '@/components/location/AddressAutocomplete';

// Sprint 5B: Device location type
interface DeviceLocation {
  lat: number;
  lng: number;
  accuracy?: number;
}

// Generate idempotency key for order placement
function generateIdempotencyKey(): string {
  return `order-${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;
}

export default function CheckoutPage() {
  const navigate = useNavigate();
  const { cart, isLoading, fetchCart, validateCart, clearCart, setBuyerLocation } = useCartStore();
  const { isOnline } = useOnlineStatus();
  const addToast = useToastStore((state) => state.addToast);
  
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [deliveryNotes, setDeliveryNotes] = useState('');
  const [paymentMethod, setPaymentMethod] = useState<'cod' | 'gcash' | 'wallet'>('cod');
  const [isValidating, setIsValidating] = useState(false);
  const [isPlacingOrder, setIsPlacingOrder] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  
  // Sprint 4O: Idempotency key to prevent double submission
  const idempotencyKeyRef = useRef<string | null>(null);
  const [priceChangedNotice, setPriceChangedNotice] = useState(false);

  // Sprint 5B: Location & Payment Policy state
  const [deviceLocation, setDeviceLocation] = useState<DeviceLocation | null>(null);
  const [deliveryCoords, setDeliveryCoords] = useState<{ lat: number; lng: number }>({ lat: 13.14, lng: 123.74 });
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [codBlockedReason, setCodBlockedReason] = useState<string | null>(null);

  useEffect(() => {
    fetchCart();
    loadSavedAddress();
    captureDeviceLocation();
  }, [fetchCart]);

  // Sprint 24: Ensure cart delivery preview uses the current pinned delivery coordinates
  // so that /api/cart can compute constitution-aligned delivery fees.
  useEffect(() => {
    if (deliveryCoords?.lat && deliveryCoords?.lng) {
      setBuyerLocation(deliveryCoords.lat, deliveryCoords.lng);
    }
  }, [deliveryCoords.lat, deliveryCoords.lng, setBuyerLocation]);

  // Sprint 5B: Capture device location for risk assessment
  const captureDeviceLocation = useCallback(() => {
    if (!navigator.geolocation) {
      logger.info('Geolocation not supported');
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const loc = {
          lat: position.coords.latitude,
          lng: position.coords.longitude,
          accuracy: position.coords.accuracy,
        };
        setDeviceLocation(loc);
        logger.info('Device location captured', { lat: loc.lat.toFixed(4), lng: loc.lng.toFixed(4) });
      },
      (error) => {
        logger.warn('Failed to get device location', { code: error.code, message: error.message });
      },
      { enableHighAccuracy: false, timeout: 10000, maximumAge: 300000 }
    );
  }, []);

  const loadSavedAddress = () => {
    const saved = localStorage.getItem('deliveryAddress');
    if (saved) {
      setDeliveryAddress(saved);
    }
    // Load saved coordinates too
    const savedCoords = localStorage.getItem('deliveryCoords');
    if (savedCoords) {
      try {
        const coords = JSON.parse(savedCoords);
        if (coords.lat && coords.lng) {
          setDeliveryCoords(coords);
        }
      } catch {
        // Ignore invalid JSON
      }
    }
  };

  // Sprint 5B: Handle address selection from autocomplete
  const handleAddressSelect = (address: AddressData | null) => {
    if (!address) return;
    setDeliveryAddress(address.fullText);
    if (address.lat && address.lng) {
      setDeliveryCoords({ lat: address.lat, lng: address.lng });
      localStorage.setItem('deliveryCoords', JSON.stringify({ lat: address.lat, lng: address.lng }));
      setBuyerLocation(address.lat, address.lng);
    }
    // Save structured data
    if (address.barangay) {
      setDeliveryNotes(`Barangay: ${address.barangay}`);
    }
  };

  // Sprint 5B: Handle map pin location change
  const handleLocationChange = (location: LocationData) => {
    setDeliveryCoords({ lat: location.lat, lng: location.lng });
    localStorage.setItem('deliveryCoords', JSON.stringify({ lat: location.lat, lng: location.lng }));
    setBuyerLocation(location.lat, location.lng);
  };

  // Sprint 5B: Handle map pin confirmation
  const handleLocationConfirm = () => {
    setShowMapPicker(false);
    addToast({ type: 'success', message: '📍 Delivery location updated!' });
  };

  const handleValidateCart = async () => {
    setIsValidating(true);
    setValidationErrors([]);

    try {
      const result = await validateCart();
      if (!result.is_valid) {
        setValidationErrors(result.errors);
      }
      return result.is_valid;
    } catch (error) {
      logger.error('Cart validation failed', error instanceof Error ? error : new Error(String(error)));
      setValidationErrors(['Failed to validate cart. Please try again.']);
      return false;
    } finally {
      setIsValidating(false);
    }
  };

  const handlePlaceOrder = async () => {
    // Sprint 4O: Block if offline
    if (!isOnline) {
      addToast({
        type: 'error',
        message: 'You are offline. Please connect to the internet to place your order.',
      });
      return;
    }

    // Sprint 4O: Prevent double-tap with idempotency key
    if (isPlacingOrder) {
      addToast({
        type: 'info',
        message: 'We\'re already processing this order. Please wait...',
      });
      return;
    }

    logger.info('Place order clicked', { 
      hasAddress: !!deliveryAddress.trim(),
      cartItemCount: cart?.items?.length || 0,
      hasCrossZone: cart?.has_cross_zone_items,
    });

    // Validate inputs
    if (!deliveryAddress.trim()) {
      setValidationErrors(['Please enter a delivery address']);
      return;
    }

    if (!cart || cart.items.length === 0) {
      setValidationErrors(['Cart is empty']);
      return;
    }

    // Validate cart
    const isValid = await handleValidateCart();
    if (!isValid) {
      return;
    }

    // Sprint 4O: Generate idempotency key if not exists
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = generateIdempotencyKey();
    }

    setIsPlacingOrder(true);
    setValidationErrors([]);

    try {
      // Save address for future use
      localStorage.setItem('deliveryAddress', deliveryAddress);

      // For multi-vendor checkout, create separate orders per vendor
      const createdOrders = [];

      for (const vendorTotal of cart.vendor_totals) {
        const orderData = {
          vendor_id: vendorTotal.vendor_id,
          address: {
            line1: deliveryAddress,
            barangay: deliveryNotes || 'N/A',
            city: 'Legazpi City',
            lat: deliveryCoords.lat,
            lng: deliveryCoords.lng,
          },
          cart: {
            items: vendorTotal.items.map(item => ({
              product_id: item.product_id,
              qty: item.quantity,
              options: [],
            })),
          },
          payment_method: paymentMethod,
          idempotency_key: `${idempotencyKeyRef.current}-${vendorTotal.vendor_id}`,
          // Sprint 5B: Include device location for risk assessment
          device_location: deviceLocation ? {
            lat: deviceLocation.lat,
            lng: deviceLocation.lng,
          } : undefined,
        };

        logger.info('Sending order', { vendorId: vendorTotal.vendor_id });

        const order = await ordersApi.createOrder(orderData);
        createdOrders.push(order);
        logger.info('Order created for vendor', { orderId: order.id, vendorId: vendorTotal.vendor_id });
      }

      // Use the first order ID for navigation
      const order = createdOrders[0];

      logger.info('Order placed successfully', { orderId: order.id });

      // Clear cart
      await clearCart();
      
      // Clear idempotency key
      idempotencyKeyRef.current = null;

      // Show success toast
      addToast({
        type: 'success',
        message: 'Order placed successfully! 🎉',
      });

      // Navigate to order confirmation
      navigate(`/orders/${order.id}`, {
        state: { orderPlaced: true },
      });
    } catch (error: any) {
      logger.error('Failed to place order', error instanceof Error ? error : new Error(String(error)), {
        responseStatus: String(error.response?.status || 'unknown'),
      });
      
      // Sprint 4O: User-friendly error messages
      let errorMessage = 'Something went wrong placing your order. Your cart is still saved — please try again.';
      
      if (error.response?.status === 400) {
        // Validation error
        const errorCode = error.response?.data?.error;
        const serverError = error.response?.data?.error?.message || error.response?.data?.message;
        const details = error.response?.data?.details;
        
        if (errorCode === 'OUT_OF_ZONE' || errorCode === 'OUT_OF_ZONE_HARD') {
          const distanceKm = details?.distanceKm;
          errorMessage = `📍 The market zone is too far from your delivery address${distanceKm ? ` (${distanceKm.toFixed(1)}km away)` : ''}. Please choose a vendor closer to your delivery location, or update your delivery address.`;
        } else if (errorCode === 'OUT_OF_ZONE_SOFT') {
          errorMessage = '📍 Your delivery is in a remote area. Online payment is required for this order.';
          // Auto-switch to GCash if COD was selected
          if (paymentMethod === 'cod') {
            setPaymentMethod('gcash');
          }
        } else if (errorCode === 'PAYMENT_METHOD_NOT_ALLOWED') {
          const blockedReason = details?.blockedReason;
          const allowedMethods = details?.allowedMethods;
          
          if (blockedReason === 'DISTANCE_OVER_LIMIT') {
            errorMessage = '💳 Cash on Delivery is not available for deliveries this far. Please use GCash or another online payment method.';
          } else if (blockedReason === 'LOCATION_MISMATCH') {
            errorMessage = '💳 Cash on Delivery is not available when ordering from a remote location. Please use GCash or another online payment.';
          } else if (blockedReason === 'AMOUNT_OVER_LIMIT') {
            errorMessage = '💳 Cash on Delivery is not available for orders over ₱5,000. Please use GCash or another online payment.';
          } else {
            errorMessage = '💳 This payment method is not available. Please choose a different payment option.';
          }
          
          // Update payment options and auto-switch
          if (allowedMethods?.length > 0 && !allowedMethods.includes(paymentMethod)) {
            setPaymentMethod(allowedMethods[0] as any);
            setCodBlockedReason(blockedReason);
          }
        } else if (serverError?.includes('out of stock')) {
          errorMessage = 'Some items in your cart are now out of stock. Please review your cart.';
        } else if (serverError?.includes('price')) {
          errorMessage = 'Some prices have changed. Please review your cart totals.';
          setPriceChangedNotice(true);
        } else if (errorCode === 'VENDOR_CLOSED') {
          errorMessage = '🚫 This vendor is currently offline. Please try again later or choose another vendor.';
        } else if (errorCode === 'VENDOR_AT_CAPACITY') {
          errorMessage = '⏳ This vendor is very busy right now. Please try again in a few minutes.';
        } else if (serverError) {
          errorMessage = serverError;
        }
      } else if (error.response?.status === 402) {
        errorMessage = 'We couldn\'t confirm your payment. You weren\'t charged. Please try again.';
      } else if (error.response?.status === 503 || !isOnline) {
        errorMessage = 'Our servers are busy. Please try again in a moment.';
      }
      
      setValidationErrors([errorMessage]);
      addToast({
        type: 'error',
        message: errorMessage,
      });
      
      // Allow retry with same idempotency key
    } finally {
      setIsPlacingOrder(false);
    }
  };

  if (isLoading && !cart) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading checkout...</p>
        </div>
      </div>
    );
  }

  if (!cart || cart.items.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md px-4">
          <Package className="w-24 h-24 text-gray-300 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">No items to checkout</h2>
          <p className="text-gray-600 mb-6">Add items to your cart before proceeding to checkout</p>
          <button
            onClick={() => navigate('/search')}
            className="bg-green-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-green-700 transition-colors"
          >
            Start Shopping
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate('/cart')}
              className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              title="Back to Cart"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <div className="flex-1">
              <h1 className="text-2xl font-bold">Checkout</h1>
              <p className="text-sm text-gray-600 mt-1">
                {cart.vendor_totals.length > 1 ? 'Multi-vendor order' : 'Single vendor order'}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="lg:grid lg:grid-cols-3 lg:gap-6">
          {/* Checkout Form */}
          <div className="lg:col-span-2 space-y-4">
            {/* Validation Errors */}
            {validationErrors.length > 0 && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-medium text-red-900 mb-2">Please fix the following issues:</h3>
                    <ul className="text-sm text-red-700 space-y-1">
                      {validationErrors.map((error, index) => (
                        <li key={index}>• {error}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Cross-Zone Warning */}
            {cart.has_cross_zone_items && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <h3 className="font-medium text-yellow-900 mb-1">
                      Cannot proceed with checkout
                    </h3>
                    <p className="text-sm text-yellow-700">
                      Your cart contains items from multiple zones. Please remove items from other zones.
                    </p>
                    <button
                      onClick={() => navigate('/cart')}
                      className="mt-2 text-sm text-yellow-900 underline hover:text-yellow-800"
                    >
                      Go to cart
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Delivery Address - Sprint 5B Enhanced */}
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <MapPin className="w-5 h-5 text-green-600" />
                  <h3 className="font-bold text-lg">Delivery Address</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setShowMapPicker(true)}
                  className="flex items-center gap-2 px-3 py-1.5 text-sm bg-green-50 text-green-700 rounded-lg hover:bg-green-100 transition-colors"
                >
                  <MapPinned className="w-4 h-4" />
                  Pin on Map
                </button>
              </div>

              {/* Address Autocomplete */}
              <AddressAutocomplete
                onChange={handleAddressSelect}
                placeholder="Search for your address..."
                className="mb-3"
                countryCode="ph"
              />

              <textarea
                value={deliveryAddress}
                onChange={(e) => setDeliveryAddress(e.target.value)}
                placeholder="Or type your complete delivery address manually"
                rows={3}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                required
              />

              {/* Show pinned coordinates */}
              {deliveryCoords && (
                <div className="mt-2 flex items-center gap-2 text-xs text-gray-500">
                  <Navigation className="w-3 h-3" />
                  <span>
                    Pinned: {deliveryCoords.lat.toFixed(5)}, {deliveryCoords.lng.toFixed(5)}
                  </span>
                  {deviceLocation && (
                    <span className="text-green-600 ml-2">
                      • Device location captured
                    </span>
                  )}
                </div>
              )}

              <input
                type="text"
                value={deliveryNotes}
                onChange={(e) => setDeliveryNotes(e.target.value)}
                placeholder="Delivery notes (landmark, gate code, etc.)"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent mt-3"
              />
            </div>

            {/* Map Picker Modal */}
            {showMapPicker && (
              <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center">
                <div className="bg-white rounded-t-xl sm:rounded-xl w-full sm:max-w-2xl max-h-[95vh] sm:max-h-[90vh] overflow-auto sm:m-4">
                  <div className="sticky top-0 bg-white p-4 border-b flex items-center justify-between z-10">
                    <h3 className="font-bold text-lg">📍 Pin Delivery Location</h3>
                    <button
                      onClick={() => setShowMapPicker(false)}
                      className="p-2 hover:bg-gray-100 rounded-full text-xl"
                    >
                      ✕
                    </button>
                  </div>
                  <div className="p-4">
                    <LocationPicker
                      initialLat={deliveryCoords.lat}
                      initialLng={deliveryCoords.lng}
                      onLocationChange={handleLocationChange}
                      onConfirm={handleLocationConfirm}
                      showConfirmButton={true}
                      height="50vh"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Payment Method - Sprint 5B Enhanced */}
            <div className="bg-white rounded-lg border p-6">
              <div className="flex items-center gap-3 mb-4">
                <CreditCard className="w-5 h-5 text-green-600" />
                <h3 className="font-bold text-lg">Payment Method</h3>
              </div>

              {/* COD Blocked Notice */}
              {codBlockedReason && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                  <strong>Note:</strong> Cash on Delivery is not available for this order
                  {codBlockedReason === 'DISTANCE_OVER_LIMIT' && ' (delivery too far)'}
                  {codBlockedReason === 'LOCATION_MISMATCH' && ' (ordering from remote location)'}
                  {codBlockedReason === 'AMOUNT_OVER_LIMIT' && ' (order over ₱5,000)'}
                  . Please use an online payment method.
                </div>
              )}

              <div className="space-y-3">
                <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer transition-colors ${
                  codBlockedReason ? 'opacity-50 bg-gray-50 cursor-not-allowed' : 'hover:bg-gray-50'
                } ${paymentMethod === 'cod' && !codBlockedReason ? 'border-green-500 bg-green-50' : ''}`}>
                  <input
                    type="radio"
                    name="payment"
                    value="cod"
                    checked={paymentMethod === 'cod'}
                    onChange={(e) => !codBlockedReason && setPaymentMethod(e.target.value as any)}
                    className="w-4 h-4 text-green-600"
                    disabled={!!codBlockedReason}
                  />
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                      Cash on Delivery
                      {codBlockedReason && <span className="text-xs bg-red-100 text-red-700 px-2 py-0.5 rounded">Unavailable</span>}
                    </div>
                    <div className="text-sm text-gray-600">
                      {codBlockedReason ? 'Not available for this order' : 'Pay when you receive your order'}
                    </div>
                  </div>
                </label>

                <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                  paymentMethod === 'gcash' ? 'border-green-500 bg-green-50' : ''
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="gcash"
                    checked={paymentMethod === 'gcash'}
                    onChange={(e) => setPaymentMethod(e.target.value as any)}
                    className="w-4 h-4 text-green-600"
                  />
                  <div className="flex-1">
                    <div className="font-medium flex items-center gap-2">
                      GCash
                      <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded">Recommended</span>
                    </div>
                    <div className="text-sm text-gray-600">Pay securely with GCash</div>
                  </div>
                </label>

                <label className={`flex items-center gap-3 p-4 border-2 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
                  paymentMethod === 'wallet' ? 'border-green-500 bg-green-50' : ''
                }`}>
                  <input
                    type="radio"
                    name="payment"
                    value="wallet"
                    checked={paymentMethod === 'wallet'}
                    onChange={(e) => setPaymentMethod(e.target.value as any)}
                    className="w-4 h-4 text-green-600"
                  />
                  <div className="flex-1">
                    <div className="font-medium">FrogBasket Wallet</div>
                    <div className="text-sm text-gray-600">Use your wallet balance</div>
                  </div>
                </label>
              </div>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1 mt-4 lg:mt-0">
            <div className="bg-white rounded-lg border p-6 sticky top-20">
              <h3 className="font-bold text-lg mb-4">Order Summary</h3>

              {/* Vendor List */}
              <div className="mb-4 space-y-2">
                <div className="text-sm font-medium text-gray-700 mb-2">Ordering from:</div>
                {cart.vendor_totals.map((vendor) => (
                  <div key={vendor.vendor_id} className="text-sm bg-gray-50 rounded p-2">
                    <div className="font-medium">{vendor.vendor_name}</div>
                    <div className="text-gray-600">
                      {vendor.item_count} item{vendor.item_count !== 1 ? 's' : ''} • ₱
                      {vendor.subtotal.toFixed(2)}
                    </div>
                  </div>
                ))}
              </div>

              <div className="space-y-3 mb-4 border-t pt-4">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal:</span>
                  <span className="font-medium">₱{cart.subtotal.toFixed(2)}</span>
                </div>

                {/* Platform Fee (12% commission) */}
                {cart.platform_fee > 0 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">Service Fee (12%):</span>
                    <span className="font-medium">₱{cart.platform_fee.toFixed(2)}</span>
                  </div>
                )}

                {/* Delivery Fee Breakdown */}
                {cart.delivery_fee_breakdown ? (
                  <>
                    <div className="border-t pt-3 space-y-2">
                      <div className="flex justify-between text-sm">
                        <span className="text-gray-600">Base Delivery Fee:</span>
                        <span>₱{cart.delivery_fee_breakdown.base_fee.toFixed(2)}</span>
                      </div>
                      {cart.delivery_fee_breakdown.extra_km_fee > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Extra Distance (+{cart.delivery_fee_breakdown.extra_km}km):
                          </span>
                          <span>₱{cart.delivery_fee_breakdown.extra_km_fee.toFixed(2)}</span>
                        </div>
                      )}
                      {cart.delivery_fee_breakdown.extra_weight_fee > 0 && (
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">
                            Extra Weight (+{cart.delivery_fee_breakdown.extra_weight}kg):
                          </span>
                          <span>₱{cart.delivery_fee_breakdown.extra_weight_fee.toFixed(2)}</span>
                        </div>
                      )}
                    </div>

                    <div className="border-t pt-3 flex justify-between">
                      <span className="font-medium">Delivery Fee:</span>
                      <span className="font-bold text-green-600">
                        ₱{cart.delivery_fee_breakdown.total_fee.toFixed(2)}
                      </span>
                    </div>

                    <div className="text-xs text-gray-500 bg-gray-50 rounded p-2">
                      <div>📍 {cart.delivery_fee_breakdown.distance_km} km</div>
                      <div>📦 {cart.total_weight_kg} kg</div>
                    </div>
                  </>
                ) : (
                  <div className="border-t pt-3 flex justify-between">
                    <span className="font-medium">Delivery Fee:</span>
                    <span className="text-sm text-gray-500">Select a pinned location to compute</span>
                  </div>
                )}
              </div>

              <div className="border-t pt-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="font-bold text-lg">Total:</span>
                  <span className="font-bold text-2xl text-green-600">
                    ₱{cart.grand_total.toFixed(2)}
                  </span>
                </div>
              </div>

              {/* Sprint 4O: Offline warning */}
              {!isOnline && (
                <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-center gap-2 text-amber-700 text-sm">
                  <WifiOff className="w-4 h-4 flex-shrink-0" />
                  <span>You're offline. Connect to the internet to place your order.</span>
                </div>
              )}

              {/* Sprint 4O: Price changed notice */}
              {priceChangedNotice && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg flex items-center gap-2 text-blue-700 text-sm">
                  <RefreshCw className="w-4 h-4 flex-shrink-0" />
                  <span>Some prices changed since you added items. We've updated your cart totals.</span>
                </div>
              )}

              <button
                onClick={handlePlaceOrder}
                disabled={
                  !isOnline ||
                  cart.has_cross_zone_items ||
                  isPlacingOrder ||
                  isValidating ||
                  !deliveryAddress.trim()
                }
                className="w-full bg-green-600 text-white py-3 rounded-lg font-bold hover:bg-green-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isPlacingOrder ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    <span>Placing Order...</span>
                  </>
                ) : !isOnline ? (
                  <>
                    <WifiOff className="w-5 h-5" />
                    <span>Offline</span>
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-5 h-5" />
                    <span>Place Order</span>
                  </>
                )}
              </button>

              {!deliveryAddress.trim() && isOnline && (
                <p className="text-xs text-red-600 text-center mt-2">
                  Please enter a delivery address
                </p>
              )}

              {cart.has_cross_zone_items && (
                <p className="text-xs text-red-600 text-center mt-2">
                  Cannot checkout with items from different zones
                </p>
              )}

              <p className="text-xs text-gray-500 text-center mt-3">
                By placing this order, you agree to our Terms & Conditions
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
